import React, { useEffect, useState } from 'react';
import type { FormInstance } from 'antd';
import { Form, Input, Row, Col, DatePicker, Button, Select } from 'antd';
import { Plus, Trash2, ChevronDown } from 'lucide-react';
import { departmentsService } from '@/services/departments.service';
import styles from '../../pages/UpdatePersonalProfilePage.module.css';

const { Option } = Select;

interface UpdateWorkInfoSectionProps {
  /**
   * userId của nhân sự cần hiển thị kinh nghiệm làm việc.
   * - Nếu truyền vào: ưu tiên dùng userId này (dùng cho màn Quản lý nhân sự chỉnh hồ sơ staff).
   * - Nếu không truyền: fallback về user hiện tại từ AuthContext (dùng cho trang hồ sơ cá nhân tự cập nhật).
   */
  userId?: number;
  form: FormInstance;
}

export const UpdateWorkInfoSection: React.FC<UpdateWorkInfoSectionProps> = () => {
  const [departments, setDepartments] = useState<Array<{ id: number; name: string }>>([]);
  const [loadingDepartments, setLoadingDepartments] = useState(false);

  useEffect(() => {
    const fetchDepartments = async () => {
      try {
        setLoadingDepartments(true);
        const response = await departmentsService.getDepartments({ limit: 100 });
        if (response?.data) {
          setDepartments(
            response.data.map((dept: any) => ({
              id: Number(dept.id),
              name: dept.name,
            }))
          );
        }
      } catch (error) {
        // Không hiển thị message vì không critical cho form này
        // eslint-disable-next-line no-console
        console.error('Error fetching departments in UpdateWorkInfoSection:', error);
      } finally {
        setLoadingDepartments(false);
      }
    };

    fetchDepartments();
  }, []);

  return (
    <div className={styles.section}>
      {/* Công việc hiện tại */}
      <div className={styles.workSectionGroup}>
        <div className={styles.workCard}>
          <div className={styles.workCardHeader}>
            <span className={styles.workCardSubtitle}>Công việc hiện tại</span>
          </div>

          {/* Công ty, Phòng ban */}
          <Row gutter={24} className={styles.formRow}>
            <Col span={12}>
              <Form.Item name="currentCompany" label="Công ty">
                <Input
                  placeholder="Tên công ty"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="currentDepartment" label="Phòng ban">
                <Select
                  placeholder="Chọn phòng ban"
                  size="large"
                  loading={loadingDepartments}
                  className={styles.select}
                  suffixIcon={<ChevronDown size={16} className={styles.dropdownIcon} />}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {departments.map((dept) => (
                    <Option key={dept.id} value={dept.id}>
                      {dept.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* Chức vụ, Vị trí */}
          <Row gutter={24} className={styles.formRow}>
            <Col span={12}>
              <Form.Item name="currentTitle" label="Chức vụ">
                <Input
                  placeholder="Chọn chức vụ"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="currentPosition" label="Vị trí">
                <Input
                  placeholder="Nhập vị trí"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>
            </Col>
          </Row>

          {/* Người tham chiếu, Mức lương, Ngày vào làm */}
          <Row gutter={24} className={styles.formRow}>
            <Col span={8}>
              <Form.Item name="currentReference" label="Người tham chiếu">
                <Input
                  placeholder="Người tham chiếu"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currentSalary" label="Mức lương">
                <Input
                  placeholder="Mức lương"
                  size="large"
                  className={styles.input}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="currentStartDate" label="Ngày vào làm">
                <DatePicker
                  placeholder="Chọn ngày"
                  size="large"
                  format="DD/MM/YYYY"
                  className={styles.fullWidth}
                />
              </Form.Item>
            </Col>
          </Row>

        </div>
      </div>

      {/* Kinh nghiệm trước đây */}
      <Form.List name="workExperiences">
        {(fields, { add, remove }) => (
          <div className={styles.workSectionGroup}>

            {fields.length === 0 && (
              <div className={styles.workEmptyState}>
                <div style={{ marginBottom: 16 }}>
                  Chưa có kinh nghiệm nào. Nhấn &quot;Thêm kinh nghiệm&quot; để bắt đầu.
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="primary"
                    icon={<Plus size={16} />}
                    size="middle"
                    className={styles.workExperienceAddButton}
                    onClick={() => add()}
                  >
                    Thêm kinh nghiệm
                  </Button>
                </div>
              </div>
            )}

            {fields.map((field, index) => (
              <div key={field.key} className={styles.workCard}>
                <div className={styles.workCardHeader}>
                  <span className={styles.workCardSubtitle}>Kinh nghiệm {index + 1}</span>
                  <Button
                    type="text"
                    danger
                    icon={<Trash2 size={16} />}
                    className={styles.workCardRemoveButton}
                    onClick={() => remove(field.name)}
                  >
                    Xóa
                  </Button>
                </div>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item
                      name={[field.name, 'fromDate']}
                      label="Từ ngày"
                    >
                      <DatePicker
                        placeholder="Chọn ngày"
                        size="large"
                        format="DD/MM/YYYY"
                        className={styles.fullWidth}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={[field.name, 'toDate']}
                      label="Đến ngày"
                    >
                      <DatePicker
                        placeholder="Chọn ngày"
                        size="large"
                        format="DD/MM/YYYY"
                        className={styles.fullWidth}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item
                      name={[field.name, 'company']}
                      label="Công ty"
                    >
                      <Input
                        placeholder="Tên công ty"
                        size="large"
                        className={styles.input}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={[field.name, 'department']}
                      label="Phòng ban"
                    >
                      <Input
                        placeholder="Nhập phòng ban"
                        size="large"
                        className={styles.input}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item
                      name={[field.name, 'title']}
                      label="Chức vụ"
                    >
                      <Input
                        placeholder="Nhập chức vụ"
                        size="large"
                        className={styles.input}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={[field.name, 'position']}
                      label="Vị trí"
                    >
                      <Input
                        placeholder="Nhập vị trí"
                        size="large"
                        className={styles.input}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24} className={styles.formRow}>
                  <Col span={12}>
                    <Form.Item
                      name={[field.name, 'reference']}
                      label="Người tham chiếu"
                    >
                      <Input
                        placeholder="Người tham chiếu"
                        size="large"
                        className={styles.input}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      name={[field.name, 'salary']}
                      label="Mức lương"
                    >
                      <Input
                        placeholder="Mức lương"
                        size="large"
                        className={styles.input}
                      />
                    </Form.Item>
                  </Col>
                </Row>

              </div>
            ))}

            {/* Nút Thêm kinh nghiệm ở cuối danh sách */}
            {fields.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
                <Button
                  type="primary"
                  icon={<Plus size={16} />}
                  size="middle"
                  className={styles.workExperienceAddButton}
                  onClick={() => add()}
                >
                  Thêm kinh nghiệm
                </Button>
              </div>
            )}
          </div>
        )}
      </Form.List>
    </div>
  );
};
